import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Container, Paper, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Chip, TextField, MenuItem, Grid, CircularProgress, FormControlLabel, Switch, Button, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Assessment as LedgerIcon } from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, where, limit, documentId } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { appendEntry } from '../utils/ledgerService';
import NumericField from './NumericField';

const ledgerCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const accountTypeColors = {
  vendor: { backgroundColor: '#E8F5E9', color: '#2E7D32', borderColor: '#81C784' },
  partner: { backgroundColor: '#E3F2FD', color: '#1565C0', borderColor: '#64B5F6' },
  company: { backgroundColor: '#FFF3E0', color: '#E65100', borderColor: '#FFB74D' },
};

const partnerSubAccountLabels = {
  lead: 'Lead',
  sales: 'Sales',
  support: 'Sales Support',
  client_pm: 'Client Relations',
  team_pm: 'Team Project Manager',
  administration: 'Administration',
  company: 'Company',
};

const LedgerEntriesTable = React.memo(function LedgerEntriesTable({ loading, rows, showSubAttribution, page, setPage, filters }) {
  const columnCount = showSubAttribution ? 8 : 7;
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const tableRef = useRef(null);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1));

  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollTop = 0;
  }, [currentPage, rowsPerPage, filters]);

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">Ledger Entries</Typography>
      </Box>
      <TableContainer ref={tableRef} sx={{ flex: 1, minHeight: 0 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Direct Account</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Event</TableCell>
              {showSubAttribution && <TableCell>Sub-account</TableCell>}
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columnCount} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={columnCount} align="center">No entries</TableCell></TableRow>
            ) : rows.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage).map((row) => {
              const typeColors = accountTypeColors[row.directAccountType] || {};
              return (
                <TableRow key={row.id} hover>
                  <TableCell>{row.postedAt}</TableCell>
                  <TableCell>{row.directAccountName}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.directAccountType}
                      size="small"
                      sx={{
                        ...typeColors,
                        border: '1px solid',
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.sourceLabel}</TableCell>
                  <TableCell>{row.eventLabel}</TableCell>
                  {showSubAttribution && (
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{row.subAccountLabel}</Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" noWrap>{row.description}</Typography>
                      {row.contractId && (
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Link
                            component={RouterLink}
                            to={{ pathname: '/contracts', search: `?openContractId=${row.contractId}`, state: { openContractId: row.contractId } }}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            Open Contract
                          </Link>
                          {row.clientName && (
                            <Box component="span" sx={{ color: 'text.secondary' }}>{row.clientName}</Box>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color={row.amount >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                      {row.amount >= 0 ? '+' : ''}{row.amountLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={rows.length} page={currentPage} rowsPerPage={rowsPerPage} sx={{ flexShrink: 0 }}
        onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} />
    </Paper>
  );
});

export default function Ledger() {
  const { currentUser, isSuperAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ directAccountId: '', source: '', eventType: '', directAccountType: '', sign: '' });
  const [page, setPage] = useState(0);
  const updateFilter = (name, value) => {
    setFilters((previous) => ({ ...previous, [name]: value }));
    setPage(0);
  };
  const [showSubAttribution, setShowSubAttribution] = useState(false);
  const [entities, setEntities] = useState([]);
  const [openManualDialog, setOpenManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({ date: new Date().toISOString().slice(0,10), amountUsd: null, directAccountType: 'company', directAccountId: '', memo: '' });
  const [contractMeta, setContractMeta] = useState({});
  const [clientNames, setClientNames] = useState({});

  const fetchEntities = async () => {
    try {
      const snap = await getDocs(collection(db, 'entities'));
      setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {}
  };

  const getEntityDisplayName = (entity) => {
    if (!entity) return '';
    return entity.entityName || entity.name || entity.legalName || entity.label || entity.title || entity.displayName || '';
  };

  const entityMap = useMemo(() => {
    const map = new Map();
    (entities || []).forEach((entity) => {
      if (!entity || !entity.id) return;
      map.set(entity.id, { ...entity, entityName: getEntityDisplayName(entity) || entity.entityName });
      const subAccounts = entity.subAccounts || entity.subaccounts || entity.children || null;
      if (Array.isArray(subAccounts)) {
        subAccounts.forEach((sub) => {
          if (!sub) return;
          const subId = sub.id || sub.entityId || sub.entity_id || sub.accountId;
          if (!subId) return;
          const subName = getEntityDisplayName(sub) || subId;
          map.set(subId, { id: subId, entityName: subName, parentId: entity.id, parent: entity });
        });
      } else if (subAccounts && typeof subAccounts === 'object') {
        Object.entries(subAccounts).forEach(([subId, subValue]) => {
          if (!subId) return;
          let subName = '';
          if (typeof subValue === 'string') {
            subName = subValue;
          } else if (subValue && typeof subValue === 'object') {
            subName = getEntityDisplayName(subValue);
          }
          map.set(subId, { id: subId, entityName: subName || subId, parentId: entity.id, parent: entity });
        });
      }
    });
    return map;
  }, [entities]);

  const fetchClients = async () => {
    try {
      const snap = await getDocs(collection(db, 'clients'));
      const map = {};
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        map[d.id] = data.clientName || data.name || data.entityName || '';
      });
      setClientNames(map);
    } catch (_) {
      // ignore client load errors
    }
  };

  const fetchContractMeta = async (rows) => {
    try {
      const ids = Array.from(new Set(
        rows
          .filter(r => r.source === 'Contract' && r.contractId)
          .map(r => r.contractId)
      ));
      if (ids.length === 0) {
        setContractMeta({});
        return;
      }
      const map = {};
      for (let i = 0; i < ids.length; i += 10) {
        const chunk = ids.slice(i, i + 10);
        const qy = query(collection(db, 'contracts'), where(documentId(), 'in', chunk));
        const snap = await getDocs(qy);
        snap.docs.forEach(d => {
          const data = d.data() || {};
          map[d.id] = {
            title: data.title || '',
            clientEntityId: data.clientEntityId || '',
          };
        });
      }
      setContractMeta(map);
    } catch (_) {
      // ignore contract title load errors
    }
  };

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const qy = query(collection(db, 'ledger'), orderBy('createdAt', 'desc'), limit(2000));
      const snap = await getDocs(qy);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEntries(rows);
      await fetchContractMeta(rows);
    } catch (e) {
      console.error('Load ledger failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntities(); fetchLedger(); fetchClients(); }, []);

  const filtered = useMemo(() => {
    return entries.filter(e => {
      const matchesSource = (() => {
        if (!filters.source) return true;
        if (filters.source === 'Banking') return e.source === 'Banking' || e.source === 'Banking-E';
        return e.source === filters.source;
      })();
      if (!matchesSource) return false;

      const matchesEvent = (() => {
        if (!filters.eventType) return true;
        if (filters.eventType === 'Banking') return e.eventType === 'Banking' || e.eventType === 'Bank-E';
        return e.eventType === filters.eventType;
      })();
      if (!matchesEvent) return false;

      if (filters.directAccountId && e.directAccountId !== filters.directAccountId) return false;
      if (filters.directAccountType && e.directAccountType !== filters.directAccountType) return false;
      if (filters.sign === 'pos' && Number(e.amountUsd) < 0) return false;
      if (filters.sign === 'neg' && Number(e.amountUsd) >= 0) return false;
      return true;
    });
  }, [entries, filters]);

  // Direct accounts that have ledger entries, for the account filter
  const directAccountOptions = useMemo(() => {
    const byId = new Map();
    entries.forEach((e) => {
      if (!e.directAccountId || byId.has(e.directAccountId)) return;
      byId.set(e.directAccountId, {
        id: e.directAccountId,
        type: e.directAccountType || '',
        name: getEntityDisplayName(entityMap.get(e.directAccountId)) || e.directAccountId,
      });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, entityMap]);

  const apSummary = useMemo(() => {
    const mapByEntity = (type) => {
      const map = new Map();
      filtered.filter(e => e.directAccountType === type).forEach((r) => {
        const key = r.directAccountId;
        map.set(key, (map.get(key) || 0) + Number(r.amountUsd || 0));
      });
      return map;
    };
    const vendorMap = mapByEntity('vendor');
    const partnerMap = mapByEntity('partner');
    const companyMap = mapByEntity('company');
    const sum = (m) => Array.from(m.values()).reduce((s, v) => s + v, 0);
    return {
      vendor: sum(vendorMap),
      partner: sum(partnerMap),
      company: sum(companyMap),
      vendorMap,
      partnerMap,
      companyMap,
    };
  }, [filtered]);

  const displayRows = useMemo(() => filtered.map((entry) => {
    const directEntity = entityMap.get(entry.directAccountId);
    const contractInfo = entry.source === 'Contract' && entry.contractId ? contractMeta[entry.contractId] : null;
    const title = String(contractInfo?.title || '').trim();
    const memo = String(entry.memo || '').trim();
    const rawSubAccount = entry.subAccount;
    let subAccountLabel = '—';
    if (rawSubAccount !== null && rawSubAccount !== undefined && rawSubAccount !== '') {
      const normalizedSubAccount = String(rawSubAccount);
      const accountType = String(entry.directAccountType || '').toLowerCase();
      subAccountLabel = (
        (accountType === 'partner' && partnerSubAccountLabels[normalizedSubAccount])
        || entityMap.get(normalizedSubAccount)?.entityName
        || normalizedSubAccount
      );
    }
    const amount = Number(entry.amountUsd) || 0;
    const clientId = contractInfo?.clientEntityId;
    return {
      id: entry.id,
      postedAt: entry.postedAt,
      directAccountName: directEntity?.entityName || '—',
      directAccountType: entry.directAccountType,
      sourceLabel: entry.source === 'Banking-E' ? 'Banking' : entry.source,
      eventLabel: entry.eventType === 'Bank-E' ? 'Banking' : entry.eventType,
      subAccountLabel,
      description: title && memo ? `${title} — ${memo}` : title || memo,
      contractId: contractInfo ? entry.contractId : '',
      clientName: clientId ? String(clientNames[clientId] || '').trim() : '',
      amount,
      amountLabel: ledgerCurrencyFormatter.format(amount),
    };
  }), [filtered, entityMap, contractMeta, clientNames]);

  const manualEntityOptions = useMemo(
    () => entities.filter(entity => (
      entity.entityType === manualForm.directAccountType
      && String(entity.status || '').toLowerCase() !== 'inactive'
    )),
    [entities, manualForm.directAccountType]
  );

  const selectedManualEntity = useMemo(
    () => manualEntityOptions.find(entity => entity.id === manualForm.directAccountId) || null,
    [manualEntityOptions, manualForm.directAccountId]
  );

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LedgerIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">Ledger</Typography>
        </Box>
        {isSuperAdmin() && (
          <Button variant="outlined" onClick={()=>setOpenManualDialog(true)}>+ Add Entry</Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={directAccountOptions}
              value={directAccountOptions.find((o) => o.id === filters.directAccountId) || null}
              onChange={(_, option) => updateFilter('directAccountId', option ? option.id : '')}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                    <span>{option.name}</span>
                    <Typography component="span" variant="caption" color="text.secondary">{option.type}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Direct Account" placeholder="All accounts" />}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth size="small" label="Source" value={filters.source} onChange={(e)=>updateFilter('source', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Contract">Contract</MenuItem>
              <MenuItem value="Banking">Banking</MenuItem>
              <MenuItem value="Subscription">Subscription</MenuItem>
              <MenuItem value="Manual">Manual</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth size="small" label="Event" value={filters.eventType} onChange={(e)=>updateFilter('eventType', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Invoiced">Invoiced</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Partial">Partial</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="Banking">Banking</MenuItem>
              <MenuItem value="Subscription">Subscription</MenuItem>
              <MenuItem value="Manual">Manual</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth size="small" label="Account Type" value={filters.directAccountType} onChange={(e)=>updateFilter('directAccountType', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
              <MenuItem value="partner">Partner</MenuItem>
              <MenuItem value="company">Company</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth size="small" label="Sign" value={filters.sign} onChange={(e)=>updateFilter('sign', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pos">Credits</MenuItem>
              <MenuItem value="neg">Debits</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md>
            <FormControlLabel control={<Switch checked={showSubAttribution} onChange={(e)=>setShowSubAttribution(e.target.checked)} />} label="Show attribution" />
          </Grid>
        </Grid>
      </Paper>

      <LedgerEntriesTable
        loading={loading}
        rows={displayRows}
        showSubAttribution={showSubAttribution}
        page={page}
        setPage={setPage}
        filters={filters}
      />

      <Dialog open={openManualDialog} onClose={()=>setOpenManualDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manual Ledger Entry</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Date" type="date" value={manualForm.date} onChange={(e)=>setManualForm({ ...manualForm, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumericField fullWidth label="Amount (signed)" value={manualForm.amountUsd} onValueChange={(n)=>setManualForm({ ...manualForm, amountUsd: n })} mode="decimal" allowNegative decimals={2} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth size="small" label="Direct Account Type" value={manualForm.directAccountType} onChange={(e)=>setManualForm({ ...manualForm, directAccountType: e.target.value, directAccountId: '' })}>
                <MenuItem value="company">Company</MenuItem>
                <MenuItem value="partner">Partner</MenuItem>
                <MenuItem value="vendor">Vendor</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete size="small" fullWidth options={manualEntityOptions} getOptionLabel={(e)=>e.entityName} value={selectedManualEntity} onChange={(e,v)=>setManualForm(prev => ({ ...prev, directAccountId: v ? v.id : '' }))} renderInput={(p)=>(<TextField {...p} label="Direct Entity" placeholder="Select" size="small" />)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Memo / Description" value={manualForm.memo} onChange={(e)=>setManualForm({ ...manualForm, memo: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenManualDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={!manualForm.date || manualForm.amountUsd == null || !manualForm.directAccountId} onClick={async ()=>{
            await appendEntry({ postedAt: manualForm.date, amountUsd: Number(manualForm.amountUsd), directAccountType: manualForm.directAccountType, directAccountId: manualForm.directAccountId, source: 'Manual', eventType: 'Manual', memo: manualForm.memo, createdBy: currentUser?.uid || null });
            setOpenManualDialog(false);
            setManualForm({ date: new Date().toISOString().slice(0,10), amountUsd: null, directAccountType: 'company', directAccountId: '', memo: '' });
            fetchLedger();
          }}>Post</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
